"use client";

import { useEffect, useRef, useCallback, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Loader2, FolderOpen, Package, X, Search, TrendingUp } from "lucide-react";
import { getProductImageUrl, formatPriceValue } from "@/lib/utils";
import type { Product, Category } from "@/lib/db";

interface Suggestions {
  popularCategories: (Category & { product_count?: number })[];
  trendingCategories: { id: string; name: string; slug: string }[];
  suggestedProducts: Product[];
}

interface SearchModalProps {
  query: string;
  isOpen: boolean;
  onClose: () => void;
  products: Product[];
  categories: Category[];
  isLoading: boolean;
}

export function SearchModal({
  query,
  isOpen,
  onClose,
  products,
  categories,
  isLoading,
}: SearchModalProps) {
  const [suggestions, setSuggestions] = useState<Suggestions | null>(null);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const suggestionsLoaded = useRef(false);

  // Fetch suggestions on first open
  useEffect(() => {
    if (isOpen && !suggestionsLoaded.current) {
      suggestionsLoaded.current = true;
      setSuggestionsLoading(true);
      fetch("/api/search/suggestions")
        .then((res) => res.json())
        .then((data) => setSuggestions(data))
        .catch(() => {})
        .finally(() => setSuggestionsLoading(false));
    }
  }, [isOpen]);

  // Close on Escape only — click-outside is handled by the parent via onBlur
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const hasResults = products.length > 0 || categories.length > 0;
  const hasQuery = query.length >= 2;
  const showSuggestions = !hasQuery && !isLoading;

  return (
    <>
      {/* Modal de recherche */}
      <div
        className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-2xl border border-gray-200 z-[100] max-h-[80vh] overflow-y-auto"
      >
        {/* === SUGGESTIONS (quand pas de query) === */}
        {showSuggestions && (
          <div>
            {suggestionsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-secondary" />
              </div>
            ) : suggestions ? (
              <div className="flex flex-col md:flex-row">
                {/* Colonne gauche : recherches populaires + catégories du moment */}
                <div className="md:w-2/5 md:border-r border-gray-100 px-4 py-4">
                  {/* Recherches populaires */}
                  {suggestions.popularCategories.length > 0 && (
                    <div className="mb-5">
                      <div className="flex items-center gap-2 mb-2">
                        <Search className="h-3.5 w-3.5 text-secondary" />
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Recherches populaires
                        </h3>
                      </div>
                      <div className="space-y-0.5">
                        {suggestions.popularCategories.map((cat) => (
                          <Link
                            key={cat.id}
                            href={`/categories/${cat.slug}`}
                            onClick={onClose}
                            className="block px-2 py-1.5 text-sm text-gray-700 hover:text-secondary hover:bg-gray-50 rounded transition-colors"
                          >
                            {cat.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Catégories du moment */}
                  {suggestions.trendingCategories.length > 0 && (
                    <div>
                      <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="h-3.5 w-3.5 text-secondary" />
                        <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                          Catégories du moment
                        </h3>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {suggestions.trendingCategories.map((cat) => (
                          <Link
                            key={cat.id}
                            href={`/categories/${cat.slug}`}
                            onClick={onClose}
                            className="inline-block px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-primary/20 text-gray-700 rounded-full transition-colors"
                          >
                            {cat.name}
                          </Link>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Colonne droite : articles suggérés */}
                {suggestions.suggestedProducts.length > 0 && (
                  <div className="md:w-3/5 px-4 py-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Package className="h-3.5 w-3.5 text-secondary" />
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                        Ces articles peuvent vous intéresser
                      </h3>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {suggestions.suggestedProducts.map((product) => (
                        <Link
                          key={product.id}
                          href={`/produits/${product.slug}`}
                          onClick={onClose}
                          className="group border border-gray-100 rounded-lg p-2 hover:border-secondary/30 hover:shadow-sm transition-all"
                        >
                          <div className="relative w-full aspect-square mb-2 bg-gray-50 rounded overflow-hidden">
                            <Image
                              src={getProductImageUrl(product.image_local)}
                              alt={product.title}
                              fill
                              className="object-contain p-1"
                              sizes="120px"
                            />
                          </div>
                          <p className="text-xs text-gray-700 group-hover:text-secondary font-medium line-clamp-2 leading-tight mb-1">
                            {product.title}
                          </p>
                          <div className="flex items-baseline gap-1">
                            {!product.quote_only && product.display_rent_price_ht ? (
                              <>
                                <span className="text-sm font-bold text-secondary">
                                  {formatPriceValue(product.display_rent_price_ht)} €
                                </span>
                                <span className="text-[10px] text-gray-400">/jour</span>
                              </>
                            ) : !product.quote_only && product.price_ht ? (
                              <span className="text-sm font-bold text-secondary">
                                {formatPriceValue(product.price_ht)} €
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">Sur devis</span>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        )}

        {/* === RÉSULTATS DE RECHERCHE (quand query) === */}

        {/* Loading state */}
        {isLoading && (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-secondary" />
            <span className="ml-2 text-sm text-gray-500">Recherche en cours...</span>
          </div>
        )}

        {/* No results */}
        {!isLoading && hasQuery && !hasResults && (
          <div className="py-8 px-4 text-center">
            <Package className="h-10 w-10 mx-auto text-gray-300 mb-3" />
            <p className="text-gray-500 text-sm">
              Aucun résultat pour <span className="font-semibold">&laquo; {query} &raquo;</span>
            </p>
            <p className="text-gray-400 text-xs mt-1">
              Essayez avec d&apos;autres termes de recherche
            </p>
          </div>
        )}

        {/* Results */}
        {!isLoading && hasQuery && hasResults && (
          <div>
            {/* Categories section */}
            {categories.length > 0 && (
              <div className="px-4 py-3 border-b border-gray-100">
                <div className="flex items-center gap-2 mb-2">
                  <FolderOpen className="h-4 w-4 text-secondary" />
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Catégories
                  </h3>
                </div>
                <div className="space-y-1">
                  {categories.map((category) => (
                    <Link
                      key={category.id}
                      href={`/categories/${category.slug}${category.root_type ? `?type=${category.root_type}` : ""}`}
                      onClick={onClose}
                      className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-gray-50 transition-colors group"
                    >
                      <div className="flex-shrink-0 w-8 h-8 bg-secondary/10 rounded-full flex items-center justify-center">
                        <FolderOpen className="h-4 w-4 text-secondary" />
                      </div>
                      <span className="text-sm text-gray-700 group-hover:text-secondary font-medium">
                        {category.name}
                        {category.root_type && <span className="ml-2 text-xs font-normal text-gray-500">{category.root_type === "location" ? "Location" : category.root_type === "achat" ? "Achat" : "Service"}</span>}
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Products section */}
            {products.length > 0 && (
              <div className="px-4 py-3">
                <div className="flex items-center gap-2 mb-2">
                  <Package className="h-4 w-4 text-secondary" />
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                    Produits
                  </h3>
                </div>
                <div className="space-y-1">
                  {products.map((product) => (
                    <Link
                      key={product.id}
                      href={`/produits/${product.slug}`}
                      onClick={onClose}
                      className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-gray-50 transition-colors group"
                    >
                      {/* Product image */}
                      <div className="flex-shrink-0 w-12 h-12 bg-white border border-gray-100 rounded-md relative overflow-hidden">
                        <Image
                          src={getProductImageUrl(product.image_local)}
                          alt={product.title}
                          fill
                          className="object-contain p-1"
                          sizes="48px"
                        />
                      </div>

                      {/* Product info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-700 group-hover:text-secondary font-medium line-clamp-1">
                          {product.title}
                        </p>
                        <div className="flex items-center gap-1">
                          {!product.quote_only && product.display_rent_price_ht ? (
                            <>
                              <span className="text-sm font-bold text-secondary">
                                {formatPriceValue(product.display_rent_price_ht)} €
                              </span>
                              <span className="text-xs text-gray-400">HT/jour</span>
                            </>
                          ) : !product.quote_only && product.price_ht ? (
                            <>
                              <span className="text-sm font-bold text-secondary">
                                {formatPriceValue(product.price_ht)} €
                              </span>
                              <span className="text-xs text-gray-400">HT</span>
                            </>
                          ) : (
                            <span className="text-xs text-gray-400">Sur devis</span>
                          )}
                        </div>
                      </div>

                      {/* Selling type badge */}
                      {product.selling_type && (
                        <span
                          className={`flex-shrink-0 text-xs px-2 py-0.5 rounded-full font-medium ${
                            product.selling_type === "RENT"
                              ? "bg-secondary/10 text-secondary"
                              : "bg-primary/20 text-gray-700"
                          }`}
                        >
                          {product.selling_type === "RENT" ? "Location" : "Achat"}
                        </span>
                      )}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Footer: "Voir tous les résultats" */}
            <div className="px-4 py-3 border-t border-gray-100 bg-gray-50 rounded-b-lg">
              <Link
                href={`/recherche?q=${encodeURIComponent(query)}`}
                onClick={onClose}
                className="block w-full text-center text-sm font-medium text-secondary hover:text-secondary/80 transition-colors py-1"
              >
                Voir tous les résultats pour &laquo; {query} &raquo;
              </Link>
            </div>
          </div>
        )}

        {/* Close button (mobile) */}
        <button
          onClick={onClose}
          className="absolute top-2 right-2 md:hidden p-1 rounded-full hover:bg-gray-100"
          aria-label="Fermer la recherche"
        >
          <X className="h-4 w-4 text-gray-400" />
        </button>
      </div>
    </>
  );
}
